import { supabase } from '../lib/supabase';
import LogoStorageService from './logoStorageService';

export class QRCodeEditService {
  /**
   * Create new QR code with enhanced error handling
   */
  static async createQRCode(qrCodeData) {
    try {
      // Validate and process logo URL if present
      if (qrCodeData?.metadata?.styling?.logoUrl) {
        const logoResult = await this.validateAndProcessLogo(qrCodeData?.metadata?.styling?.logoUrl);
        if (logoResult?.processedUrl) {
          qrCodeData.metadata.styling.logoUrl = logoResult?.processedUrl;
        }
      }

      const { data, error } = await supabase
        ?.from('qr_codes')
        ?.insert([{
          ...qrCodeData,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select('*')
        ?.single();

      if (error) {
        throw new Error(error?.message);
      }

      return {
        success: true,
        qrCode: data
      };
    } catch (error) {
      console.log('QR Code creation error:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Update existing QR code with enhanced error handling and immediate effect
   */
  static async updateQRCode(qrCodeId, qrCodeData) {
    try {
      // Validate and process logo URL if present
      if (qrCodeData?.metadata?.styling?.logoUrl) {
        const logoResult = await this.validateAndProcessLogo(qrCodeData?.metadata?.styling?.logoUrl);
        if (logoResult?.processedUrl) {
          qrCodeData.metadata.styling.logoUrl = logoResult?.processedUrl;
        }
      }

      const { data, error } = await supabase
        ?.from('qr_codes')
        ?.update({
          ...qrCodeData,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', qrCodeId)
        ?.select('*')
        ?.single();

      if (error) {
        throw new Error(error?.message);
      }

      // ENHANCED: Clear redirect cache if URL was updated
      if (qrCodeData?.content && data?.short_url) {
        try {
          const { QRDomainRedirectService } = await import('./qrDomainRedirectService');
          await QRDomainRedirectService?.clearRedirectCache(data?.short_url);
        } catch (cacheError) {
          console.log('Cache clear error (non-critical):', cacheError);
        }
      }

      return {
        success: true,
        qrCode: data
      };
    } catch (error) {
      console.log('QR Code update error:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Generate new random short URL using Supabase function
   */
  static async generateRandomShortCode() {
    try {
      const { data, error } = await supabase?.rpc('generate_short_url');

      if (error) {
        throw new Error(error?.message);
      }

      return {
        success: true,
        shortCode: data
      };
    } catch (error) {
      console.log('Short code generation error:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Validate short code uniqueness
   */
  static async validateShortCode(shortCode, excludeQRCodeId = null) {
    try {
      let query = supabase
        ?.from('qr_codes')
        ?.select('id')
        ?.eq('short_url', shortCode);

      if (excludeQRCodeId) {
        query = query?.neq('id', excludeQRCodeId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error?.message);
      }

      return {
        success: true,
        isUnique: data?.length === 0,
        exists: data?.length > 0
      };
    } catch (error) {
      console.log('Short code validation error:', error);
      return {
        success: false,
        error: this.formatError(error),
        isUnique: false
      };
    }
  }

  /**
   * Validate and process logo URL to prevent NetworkError
   */
  static async validateAndProcessLogo(logoUrl) {
    try {
      if (!logoUrl || logoUrl?.startsWith('data:') || logoUrl?.includes('supabase')) {
        // Skip processing for data URLs or Supabase URLs
        return { success: true, processedUrl: logoUrl };
      }

      // Validate the URL
      const validationResult = await LogoStorageService?.validateLogoUrl?.(logoUrl);
      
      if (validationResult?.success) {
        return {
          success: true,
          processedUrl: validationResult?.useProxy ? validationResult?.url : logoUrl,
          useProxy: validationResult?.useProxy
        };
      } else {
        console.log('Logo validation failed:', validationResult?.error);
        // Return original URL as fallback but log the issue
        return {
          success: false,
          processedUrl: logoUrl,
          error: validationResult?.error,
          fallback: true
        };
      }
    } catch (error) {
      console.log('Logo processing error:', error);
      return {
        success: false,
        processedUrl: logoUrl,
        error: error?.message,
        fallback: true
      };
    }
  }

  /**
   * Format error messages for better user experience
   */
  static formatError(error) {
    const message = error?.message || 'Unknown error';
    
    if (message?.includes('NetworkError') || message?.includes('fetch')) {
      return 'Network connection failed. Please check your internet connection and try again.';
    } else if (message?.includes('CORS')) {
      return 'Logo image cannot be accessed due to security restrictions. Please upload the image directly or use a different image.';
    } else if (message?.includes('timeout')) {
      return 'Request timed out. Please try again with a smaller image or better internet connection.';
    } else if (message?.includes('duplicate') || message?.includes('unique') || message?.includes('qr_codes_short_url_key')) {
      return 'This short code is already taken. Please choose a different one or generate a random code.';
    } else if (message?.includes('foreign key')) {
      return 'Invalid data reference. Please refresh the page and try again.';
    } else if (message?.includes('not authenticated')) {
      return 'Authentication required. Please sign in again.';
    }
    
    return message;
  }

  /**
   * Delete QR code
   */
  static async deleteQRCode(qrCodeId) {
    try {
      const { error } = await supabase
        ?.from('qr_codes')
        ?.delete()
        ?.eq('id', qrCodeId);

      if (error) {
        throw new Error(error?.message);
      }

      return { success: true };
    } catch (error) {
      console.log('QR Code deletion error:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }

  /**
   * Get QR code by ID
   */
  static async getQRCode(qrCodeId) {
    try {
      const { data, error } = await supabase
        ?.from('qr_codes')
        ?.select('*')
        ?.eq('id', qrCodeId)
        ?.single();

      if (error) {
        throw new Error(error?.message);
      }

      return {
        success: true,
        qrCode: data
      };
    } catch (error) {
      console.log('QR Code fetch error:', error);
      return {
        success: false,
        error: this.formatError(error)
      };
    }
  }
}

export default QRCodeEditService;